import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate, useParams, useRouteError } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Tooltip, ConfirmProvider, ToastProvider } from "@/ui";
import App from "./App";
import { isNativeIOS } from "./lib/native-auth";
import { installUpdateListener, isStaleBuildError, showUpdateBanner } from "./lib/update-banner";
// React Email editor — theme + floating UI styles. The package's exports
// map advertises link/button/image bubble-menu CSS but the files aren't on
// disk in 1.3.5; only these four ship.
import "@react-email/editor/themes/default.css";
import "@react-email/editor/styles/bubble-menu.css";
import "@react-email/editor/styles/slash-command.css";
import "@react-email/editor/styles/inspector.css";

import "./index.css";

// Route modules are deliberately lazy: the email editor and collaborative
// note stack are substantial and should not be downloaded for every screen.
const Compose = lazy(() => import("./routes/Compose"));
const Drafts = lazy(() => import("./routes/Drafts"));
const Inbox = lazy(() => import("./routes/Inbox"));
const Sent = lazy(() => import("./routes/Sent"));
const MailView = lazy(() => import("./routes/MailView"));
const MailLabels = lazy(() => import("./routes/MailLabels"));
const PersonalGmail = lazy(() => import("./routes/PersonalGmail"));
const GmailOAuthCallback = lazy(() => import("./routes/GmailOAuthCallback"));
const Templates = lazy(() => import("./routes/Templates"));
const MailHeaderPoc = lazy(() => import("./routes/MailHeaderPoc"));
const Settings = lazy(() => import("./routes/Settings"));
const AgentRequests = lazy(() => import("./routes/AgentRequests"));
const Contacts = lazy(() => import("./routes/Contacts"));
const Companies = lazy(() => import("./routes/Companies"));
const Pipeline = lazy(() => import("./routes/Pipeline"));
const LeadDetail = lazy(() => import("./routes/LeadDetail"));
const Events = lazy(() => import("./routes/Events"));
const Calendar = lazy(() => import("./routes/Calendar"));
const Meetings = lazy(() => import("./routes/Meetings"));
const MeetingPage = lazy(() => import("./routes/MeetingPage"));
const Gifts = lazy(() => import("./routes/Gifts"));
const Shipping = lazy(() => import("./routes/Shipping"));
const Scheduling = lazy(() => import("./routes/Scheduling"));
const Today = lazy(() => import("./routes/Today"));
const LoopActions = lazy(() => import("./routes/LoopActions"));
const GoingQuiet = lazy(() => import("./routes/GoingQuiet"));
const Chat = lazy(() => import("./routes/Chat"));
const Cases = lazy(() => import("./routes/Cases"));
const BookingPage = lazy(() => import("./routes/public/BookingPage"));
const SuggestedBookingPage = lazy(() => import("./routes/public/SuggestedBookingPage"));
const ManageBookingPage = lazy(() => import("./routes/public/ManageBookingPage"));
// Built-in video room (Cloudflare RealtimeKit) — anonymous guests, heavy SDK.
const MeetingRoomPage = lazy(() => import("./routes/public/MeetingRoomPage"));
const MeetJoinPage = lazy(() => import("./routes/public/MeetJoinPage"));

// Unified public hosts: booking lives under /book/* and video under /v/*.
const MEET_HOSTS = new Set([
    "meet.kardoe.com",
    "meet.tryliveroom.com",
    "meet.attendvip.com",
    "meet.theattendapp.com",
    "meetings.theattendapp.com",
]);
const isMeetHost = MEET_HOSTS.has(location.hostname);

// Single QueryClient for the whole app. The realtime bridge mutates the
// cache surgically via `queryClient.setQueryData(["messages"], ...)`; the
// inbox/sent views read from the same key. Refetch-on-focus is off because
// the WebSocket already keeps the list current and a manual refresh on focus
// would clobber any optimistic-in-flight rows.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime:           30_000,
            refetchOnWindowFocus: false,
            retry:                1,
        },
    },
});

function RouteError() {
    const error = useRouteError();
    if (isStaleBuildError(error)) {
        // Not a failure — the deployed build moved on. Say so, don't alarm.
        showUpdateBanner();
        return <main className="route-error"><div className="auth-gate-mark">K</div><h1>There's a new version of the app</h1><p>Refresh to load it.</p><button onClick={() => window.location.reload()}>Refresh to load</button></main>;
    }
    console.error("Route render failed", error);
    return <main className="route-error"><div className="auth-gate-mark">K</div><h1>Something went wrong</h1><p>The workspace could not be displayed.</p><button onClick={() => window.location.reload()}>Reload workspace</button></main>;
}

function LegacyInboxRedirect() {
    const { threadId } = useParams();
    return <Navigate to={threadId ? `/inbox/${encodeURIComponent(threadId)}` : "/inbox"} replace />;
}

const router = createBrowserRouter([
    // The short host is guest-facing only: its root is a join box, not the
    // CRM shell (whose index route would bounce a guest to the login page).
    isMeetHost ? { path: "/", element: <MeetJoinPage />, errorElement: <RouteError /> } : {
        path: "/",
        element: <App />,
        errorElement: <RouteError />,
        children: [
            { index: true, element: <Navigate to="/inbox" replace /> },
            // The central Account UI's "Open App" link appends the org slug.
            { path: "kardoe", element: <Navigate to="/inbox" replace /> },
            { path: "inbox", element: <Inbox /> },
            { path: "inbox/:threadId", element: <Inbox /> },
            { path: "mail/personal", element: <PersonalGmail /> },
            { path: "mail/personal/:threadId", element: <PersonalGmail /> },
            { path: "oauth/google/gmail", element: <GmailOAuthCallback /> },
            { path: "mail/inbox", element: <LegacyInboxRedirect /> },
            { path: "mail/inbox/:threadId", element: <LegacyInboxRedirect /> },
            { path: "sent", element: <Sent /> },
            { path: "sent/:threadId", element: <Sent /> },
            { path: "mail/starred", element: <MailView /> },
            { path: "mail/starred/:threadId", element: <MailView /> },
            { path: "mail/unread", element: <MailView /> },
            { path: "mail/unread/:threadId", element: <MailView /> },
            { path: "mail/all", element: <MailView /> },
            { path: "mail/all/:threadId", element: <MailView /> },
            { path: "mail/archive", element: <MailView /> },
            { path: "mail/archive/:threadId", element: <MailView /> },
            { path: "mail/trash", element: <MailView /> },
            { path: "mail/trash/:threadId", element: <MailView /> },
            { path: "mail/label/:labelId", element: <MailView /> },
            { path: "mail/label/:labelId/:threadId", element: <MailView /> },
            { path: "mail/labels", element: <MailLabels /> },
            { path: "compose", element: <Navigate to="/inbox" replace state={{ compose: {} }} /> },
            { path: "compose/:templateId", element: <Navigate to="/templates" replace /> },
            { path: "campaigns/new", element: <Compose flow="campaign" /> },
            { path: "drafts", element: <Drafts /> },
            { path: "templates", element: <Templates /> },
            ...(import.meta.env.DEV ? [{ path: "mail-poc", element: <MailHeaderPoc /> }] : []),
            { path: "today",     element: <Today /> },
            { path: "day/:date", element: <Today /> },
            { path: "actions",   element: <LoopActions /> },
            { path: "quiet",     element: <GoingQuiet /> },
            { path: "chat", element: <Chat /> },
            { path: "chat/:conversationId", element: <Chat /> },
            { path: "cases", element: <Cases /> },
            { path: "cases/:caseId", element: <Cases /> },
            { path: "pipeline",  element: <Pipeline /> },
            { path: "pipeline/:leadId", element: <LeadDetail /> },
            { path: "leads",     element: <Navigate to="/pipeline" replace /> },
            { path: "contacts",  element: <Contacts /> },
            { path: "contacts/:contactId", element: <Contacts /> },
            { path: "companies", element: <Companies /> },
            { path: "events",    element: <Events /> },
            { path: "calendar",  element: <Calendar /> },
            { path: "meetings",  element: <Meetings /> },
            { path: "meetings/:meetingId", element: <MeetingPage /> },
            { path: "gifts",     element: <Gifts /> },
            { path: "shipping",  element: <Shipping /> },
            { path: "scheduling", element: <Scheduling /> },
            { path: "bookings",  element: <Navigate to="/meetings" replace /> },
            { path: "agent-requests", element: <AgentRequests /> },
            { path: "settings",  element: <Settings /> },
            { path: "settings/:section", element: <Settings /> },
        ],
    },
    // Public booking pages — SIBLINGS of the "/" <App> route so the App
    // auth-guard does NOT apply (these are reachable by anonymous invitees).
    { path: "/book/:slug", element: <BookingPage />, errorElement: <RouteError /> },
    { path: "/book/suggested/:token", element: <SuggestedBookingPage />, errorElement: <RouteError /> },
    { path: "/book/manage/:token", element: <ManageBookingPage />, errorElement: <RouteError /> },
    { path: "/v/:id", element: <MeetingRoomPage />, errorElement: <RouteError /> },
]);

document.documentElement.toggleAttribute("data-native-ios", isNativeIOS());

installUpdateListener();
createRoot(document.getElementById("root")!).render(
    <StrictMode>
        <QueryClientProvider client={queryClient}>
            <Tooltip.Provider>
                <ToastProvider>
                    <ConfirmProvider>
                        <Suspense fallback={
                            <main className="auth-gate" aria-live="polite" aria-busy="true">
                                <div className="auth-gate-mark" aria-hidden="true">K</div>
                                <p>Opening workspace…</p>
                            </main>
                        }>
                            <RouterProvider router={router} />
                        </Suspense>
                    </ConfirmProvider>
                </ToastProvider>
            </Tooltip.Provider>
        </QueryClientProvider>
    </StrictMode>,
);
